import type { Dispatch, SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import type { DailyQuestView } from '../../../models/quest';
import { isXQuestAwaitingModeration } from '../../../utils/quest/questHelpers';

export interface DailyQuestCardInfoProps {
  quest: DailyQuestView;
  t: TFunction;
  isXFollowQuest: (questId: number) => boolean;
  getXUrlForQuest: (questId: number) => string;
  calculateTimeUntilAvailable: (questId: number) => string;
  setShowVipInfoModal: Dispatch<SetStateAction<boolean>>;
  isXQuestOnModeration: (questId: number) => boolean;
}

export default function DailyQuestCardInfo({
  quest,
  t,
  isXFollowQuest,
  getXUrlForQuest,
  calculateTimeUntilAvailable,
  setShowVipInfoModal,
  isXQuestOnModeration,
}: DailyQuestCardInfoProps) {
  return (
    <>
<div className="flex flex-col gap-sm">
  <p className="m-0 [font-family:'Rubik_One',sans-serif] text-[16px] leading-[24px] font-normal tracking-[0] text-white">
    {quest.title}
  </p>
  {quest.hasLink ? (
    <>
    <p className="m-0 [font-family:Lato,sans-serif] text-[14px] leading-[16px] font-normal tracking-[0] text-white">
        {quest.description}
      </p>
      {quest.questId === 6 && (
        <div>
          <a
            href="https://t.me/web3islands"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="cursor-pointer text-accent underline"
          >
            https://t.me/web3islands
          </a>
        </div>
      )}
      {quest.questId === 108 && (
        <div>
          <a
            href="https://t.me/web3islands_chat"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="cursor-pointer text-accent underline"
          >
            https://t.me/web3islands_chat
          </a>
        </div>
      )}
        {quest.questId === 7 && (
          <span>
            {' '}
            <a
              href="https://t.me/TonFaucetCryptoBot/faucet?startapp=web3islands"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="cursor-pointer text-accent underline"
            >
              TON Faucet
            </a>
          </span>
        )}
      {isXFollowQuest(quest.questId) && (
        <a
          href={getXUrlForQuest(quest.questId)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="mt-sm inline-flex items-center gap-sm rounded-[10px] border border-accent-alpha-25 bg-accent-alpha-10 px-lg py-sm no-underline"
        >
          <img
            src="/ui/daily-quests/x-logo.png"
            alt="X"
            className="h-4 w-4 object-contain"
          />
          <span className="[font-family:Lato,sans-serif] text-[13px] font-bold text-accent">
            {getXUrlForQuest(quest.questId)}
          </span>
        </a>
      )}
    </>
  ) : (
    <p className="m-0 [font-family:Lato,sans-serif] text-[14px] leading-[16px] font-normal tracking-[0] text-white">
      {quest.description}
    </p>
  )}
</div>
{/* Показуємо таймер лише якщо квест виконаний і це не одноразовий квест (не marketing, не X follow, не repeatable) */}
{quest.isFinished && !isXFollowQuest(quest.questId) && !quest.isMarketing && !quest.isRepeatable && (
  <div className="[font-family:Lato,sans-serif] text-[14px] leading-[16px] font-normal tracking-[0] text-white">
    <span className="font-bold text-accent">
      {t("Time until quest becomes available again")}:
    </span>{' '}
    <span>
      {quest.isMarketing 
        ? '∞' 
        : calculateTimeUntilAvailable(quest.questId)}
    </span>
  </div>
)}
<div className="[font-family:Lato,sans-serif] text-[14px] leading-[16px] font-normal tracking-[0] text-white">
  <span className="font-bold text-accent">
    {t("Reward")}:
  </span>
  <br />
  {quest.rewardType === "vip" ? (
    <span className="font-bold text-gold">{t("VIP (3 days)")}</span>
  ) : (
  <span className="inline-flex flex-wrap items-center gap-0">
      <span className="inline-flex items-center">
      {quest.rewardAmount.toLocaleString('en-US')}
      <img
      src={quest.rewardType === "currency"
        ? "/ui/daily-quests/daily-quests-pearls.png"
        : "/ui/daily-rewards/daily-rewards-exp-gift.png"}
        alt=""
        className="h-10 w-10 align-middle object-contain"
      />
      </span>
      {quest.secondaryRewardAmount > 0 && (
        <span className="inline-flex items-center">
          <span className="mx-[2px] text-accent">+</span>
          {quest.secondaryRewardAmount.toLocaleString('en-US')}
          <img
            src={quest.secondaryRewardType === "currency"
              ? "/ui/daily-quests/daily-quests-pearls.png"
              : "/ui/daily-rewards/daily-rewards-exp-gift.png"}
            alt=""
            className="h-10 w-10 align-middle object-contain"
          />
        </span>
      )}
    </span>
  )}
</div>
{quest.rewardType === "vip" && (
  <div
    className="flex cursor-pointer justify-center"
    onClick={(e) => { e.stopPropagation(); setShowVipInfoModal(true); }}
  >
    <img
      src="/ui/item-icons/1.png"
      alt="VIP"
      className="h-24 w-24 rounded-md object-contain"
    />
  </div>
)}
{(() => {
  const isXQuestOnMod = isXQuestAwaitingModeration(isXFollowQuest(quest.questId), isXQuestOnModeration(quest.questId));

  // Якщо X квест на модерації і не виконаний - показуємо спрощений прогрес
  if (isXQuestOnMod) {
    return (
      <>
        <div className="relative m-0 h-0 w-full">
          <div className="absolute top-0 right-0 left-0 h-px bg-white-alpha-30" />
        </div>
        <div className="flex items-center gap-md">
          <p className="m-0 [font-family:Lato,sans-serif] text-[14px] leading-[16px] font-normal tracking-[0] text-white">
            <span className="font-bold text-accent">{t("X follow verification:")}</span>
            {' '}
            {"0/1"}
          </p>
          <span className="text-[16px]">⏳</span>
        </div>
      </>
    );
  }
  
  // Звичайний прогрес для інших квестів
  return quest.progressText && (
    <>
      <div className="relative m-0 h-0 w-full">
        <div className="absolute top-0 right-0 left-0 h-px bg-white-alpha-30" />
      </div>
      {quest.progressTaskDescription && (
        <div className="flex items-center gap-md">
          {quest.progressTaskImages && quest.progressTaskImages.length > 0 && (
            <div className="flex shrink-0 items-center gap-xs">
              {quest.progressTaskImages.map((imagePath, index) => (
                <img
                  key={index}
                  src={imagePath}
                  alt=""
                  className="h-6 w-6 object-contain"
                />
              ))}
            </div>
          )}
          <p className="m-0 [font-family:Lato,sans-serif] text-[14px] leading-[16px] font-normal tracking-[0] text-white">
            {quest.progressTaskDescription}
          </p>
        </div>
      )}
      <div className="flex items-center gap-md">
        <p className="m-0 [font-family:Lato,sans-serif] text-[14px] leading-[16px] font-normal tracking-[0] text-white">
          {quest.progressLabel ? (
            <>
              <span className="font-bold text-accent">{quest.progressLabel}</span>
              {' '}
            </>
          ) : null}
          {quest.progressText}
        </p>
        {quest.progressCompleted ? (
          <span className="text-[16px]">✅</span>
        ) : (
          <span className="text-[16px]">❌</span>
        )}
      </div>
    </>
  );
})()}
    </>
  );
}
